import { useMemo } from 'react';
import { ProductDto, ProductIngredientDto, ProductSizeDto } from '@kotipizzagroup/kotipizza-products-api-client';
import { useAllIngredients, useAllS4DIngredients, useAllS4DProducts } from '../../../apis/products-api';
import { compareProductToS4D, S4DProductComparison } from '../utils/s4dProductComparison';
import { ProductSizeModel } from '../types';

type Options = {
  product: ProductDto | undefined;
  recipe: ProductIngredientDto[];
  productSizes: ProductSizeDto[];
  sizePrices: ProductSizeModel[];
};

export const useS4DComparison = (options: Options): S4DProductComparison | undefined => {
  const { product, recipe, productSizes, sizePrices } = options;

  const { data: s4dProducts } = useAllS4DProducts();
  const { data: s4dIngredients } = useAllS4DIngredients();
  const { data: ingredients } = useAllIngredients();

  return useMemo(() => {
    if (!product || !s4dProducts || !s4dIngredients || !ingredients) return undefined;

    // Unlike the product list, the details page has the calculated prices of the recipe-priced
    // sizes in hand, so those are compared against S4D here as well.
    const ourPricesBySizeId = new Map<number, number>();
    sizePrices.forEach((size) => {
      const price = size.minimumPrice || size.calculatedPrice;
      if (price > 0) ourPricesBySizeId.set(size.sizeId, price);
    });

    return compareProductToS4D({
      product: { ...product, ingredients: recipe },
      s4dProducts,
      s4dIngredients,
      ingredients,
      productSizes,
      ourPricesBySizeId,
    });
  }, [product, recipe, productSizes, sizePrices, s4dProducts, s4dIngredients, ingredients]);
};
